import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef } from "react";
import type { TelemetrySpan } from "../../../pavilion/schemas";
import { getGroupDisplayName } from "../../lib/telemetry-utils";

interface TelemetryExpandedContentProps {
  row: TelemetrySpan;
  getParsedAttributes: (
    attributes: Record<string, unknown>,
  ) => Record<string, Record<string, unknown>>;
}

export function TelemetryExpandedContent({
  row,
  getParsedAttributes,
}: TelemetryExpandedContentProps) {
  const expandedContentRef = useRef<HTMLDivElement>(null);

  // Create sections for virtualization
  const sections = useMemo(() => {
    const items = [];

    // Basic details section
    items.push({
      id: "details",
      type: "details" as const,
      height: 120,
      content: (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="font-medium text-foreground mb-1">Details</div>
            <div className="space-y-1">
              <div>
                <span className="text-muted-foreground">Agent:</span>{" "}
                {row.agentName}
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>{" "}
                {new Date(row.createdAt).toLocaleString("en-US", {
                  timeZone: "UTC",
                  hour12: false,
                })}{" "}
                UTC
              </div>
              {row.endTime && (
                <div>
                  <span className="text-muted-foreground">End Time:</span>{" "}
                  {new Date(row.endTime).toLocaleString("en-US", {
                    timeZone: "UTC",
                    hour12: false,
                  })}{" "}
                  UTC
                </div>
              )}
              {row.parentSpanId && (
                <div>
                  <span className="text-muted-foreground">Parent Span:</span>{" "}
                  {row.parentSpanId}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">Identifiers</div>
            <div className="space-y-1">
              <div>
                <span className="text-muted-foreground">Span ID:</span>{" "}
                <code className="bg-muted px-1 rounded text-xs">
                  {row.spanId}
                </code>
              </div>
              <div>
                <span className="text-muted-foreground">Trace ID:</span>{" "}
                <code className="bg-muted px-1 rounded text-xs">
                  {row.traceId}
                </code>
              </div>
            </div>
          </div>
        </div>
      ),
    });

    // Operation details
    if (row.operationDetails) {
      items.push({
        id: "operation",
        type: "operation" as const,
        height: 100,
        content: (
          <div>
            <div className="font-medium text-foreground mb-1">
              AI Operation Details
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="space-y-1">
                {row.operationDetails.modelProvider && (
                  <div>
                    <span className="text-muted-foreground">Model:</span>{" "}
                    {row.operationDetails.modelProvider}
                    {(row.operationDetails.modelName ||
                      row.attributes?.["ai.model.id"] ||
                      row.attributes?.["gen_ai.request.model"]) && (
                      <>
                        /
                        {row.operationDetails.modelName ||
                          row.attributes?.["ai.model.id"] ||
                          row.attributes?.["gen_ai.request.model"]}
                      </>
                    )}
                  </div>
                )}
                {row.operationDetails.finishReason && (
                  <div>
                    <span className="text-muted-foreground">
                      Finish Reason:
                    </span>{" "}
                    {row.operationDetails.finishReason}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {row.operationDetails.inputTokens && (
                  <div>
                    <span className="text-muted-foreground">Input Tokens:</span>{" "}
                    {row.operationDetails.inputTokens.toLocaleString()}
                  </div>
                )}
                {row.operationDetails.outputTokens && (
                  <div>
                    <span className="text-muted-foreground">
                      Output Tokens:
                    </span>{" "}
                    {row.operationDetails.outputTokens.toLocaleString()}
                  </div>
                )}
                {row.operationDetails.totalTokens && (
                  <div>
                    <span className="text-muted-foreground">Total Tokens:</span>{" "}
                    {row.operationDetails.totalTokens.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      });
    }

    // Tool calls - each as separate item for better virtualization
    if (row.toolCalls && row.toolCalls.length > 0) {
      items.push({
        id: "toolcalls-header",
        type: "header" as const,
        height: 30,
        content: (
          <div className="font-medium text-foreground mb-1">
            Tool Calls ({row.toolCalls.length})
          </div>
        ),
      });

      row.toolCalls.forEach((toolCall, index) => {
        items.push({
          id: `toolcall-${index}`,
          type: "toolcall" as const,
          height: 150,
          content: (
            <div className="border rounded p-2 bg-background mb-2">
              <div className="flex justify-between items-start mb-1">
                <div className="font-medium text-xs">{toolCall.toolName}</div>
                <div
                  className={`text-xs px-1 rounded ${toolCall.success ? "bg-green-900/20 text-green-400" : "bg-red-900/20 text-red-400"}`}
                >
                  {toolCall.success ? "Success" : "Failed"}
                </div>
              </div>
              {toolCall.executionTime && (
                <div className="text-xs text-muted-foreground mb-1">
                  Execution Time: {toolCall.executionTime}ms
                </div>
              )}
              {toolCall.parameters &&
                Object.keys(toolCall.parameters).length > 0 && (
                  <div className="text-xs mb-2">
                    <div className="text-muted-foreground mb-1">
                      Parameters:
                    </div>
                    <div className="bg-muted p-2 rounded text-xs max-h-32 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(toolCall.parameters, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              {toolCall.result && Object.keys(toolCall.result).length > 0 && (
                <div className="text-xs mb-2">
                  <div className="text-muted-foreground mb-1">Result:</div>
                  <div className="bg-muted p-2 rounded text-xs max-h-32 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(toolCall.result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              {toolCall.errorMessage && (
                <div className="text-xs text-red-400 mt-1">
                  Error: {toolCall.errorMessage}
                </div>
              )}
            </div>
          ),
        });
      });
    }

    // Errors
    if (row.errors && row.errors.length > 0) {
      items.push({
        id: "errors-header",
        type: "header" as const,
        height: 30,
        content: <div className="font-medium text-foreground mb-1">Errors</div>,
      });

      row.errors.forEach((error, index) => {
        items.push({
          id: `error-${index}`,
          type: "error" as const,
          height: 120,
          content: (
            <div className="border rounded p-2 bg-red-900/10 border-red-900/20 mb-2">
              <div className="text-xs">
                <div className="font-medium text-red-400 mb-1">
                  {error.errorType}
                </div>
                <div className="text-red-300 mb-1">{error.errorMessage}</div>
                {error.errorStack && (
                  <pre className="bg-red-900/20 p-1 rounded text-xs overflow-x-auto text-red-300">
                    {error.errorStack}
                  </pre>
                )}
                {error.retryCount > 0 && (
                  <div className="text-red-300 mt-1">
                    Retry Count: {error.retryCount}
                  </div>
                )}
              </div>
            </div>
          ),
        });
      });
    }

    // Request-Response Info
    const parsedAttributes = getParsedAttributes(row.attributes || {});
    const requestData =
      parsedAttributes["ai.request"] || parsedAttributes["gen_ai.request"];
    const responseData =
      parsedAttributes["ai.response"] || parsedAttributes["gen_ai.response"];

    if (requestData || responseData) {
      items.push({
        id: "request-response",
        type: "request-response" as const,
        height: 200,
        content: (
          <div>
            <div className="font-medium text-foreground mb-2">
              Request-Response Info
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requestData && (
                <div>
                  <div className="font-medium text-foreground text-xs mb-1">
                    Request
                  </div>
                  <div className="space-y-1">
                    {Object.entries(requestData).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="text-muted-foreground">{key}:</span>{" "}
                        {typeof value === "object" ? (
                          <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                            <pre className="text-xs whitespace-pre-wrap">
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          </div>
                        ) : String(value).length > 100 ? (
                          <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                            <pre className="text-xs whitespace-pre-wrap font-mono">
                              {String(value)}
                            </pre>
                          </div>
                        ) : (
                          <span className="font-mono">{String(value)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {responseData && (
                <div>
                  <div className="font-medium text-foreground text-xs mb-1">
                    Response
                  </div>
                  <div className="space-y-1">
                    {Object.entries(responseData).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="text-muted-foreground">{key}:</span>{" "}
                        {typeof value === "object" ? (
                          <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                            <pre className="text-xs whitespace-pre-wrap">
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          </div>
                        ) : String(value).length > 100 ? (
                          <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                            <pre className="text-xs whitespace-pre-wrap font-mono">
                              {String(value)}
                            </pre>
                          </div>
                        ) : (
                          <span className="font-mono">{String(value)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      });
    }

    // Telemetry attributes
    if (row.attributes && Object.keys(row.attributes).length > 0) {
      const filteredAttributes = Object.entries(parsedAttributes).filter(
        ([groupKey]) =>
          !groupKey.includes("request") && !groupKey.includes("response"),
      );

      if (filteredAttributes.length > 0) {
        items.push({
          id: "attributes",
          type: "attributes" as const,
          height: 150,
          content: (
            <div>
              <div className="font-medium text-foreground mb-2">
                Telemetry Attributes
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAttributes.map(([groupKey, groupData]) => (
                  <div key={groupKey} className="space-y-2">
                    <div className="font-medium text-foreground text-xs">
                      {getGroupDisplayName(groupKey)}
                    </div>
                    <div className="space-y-1">
                      {Object.entries(groupData).map(
                        ([fieldKey, fieldValue]) => (
                          <div key={fieldKey} className="text-xs">
                            <span className="text-muted-foreground">
                              {fieldKey}:
                            </span>{" "}
                            {typeof fieldValue === "object" ? (
                              <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                                <pre className="text-xs whitespace-pre-wrap">
                                  {JSON.stringify(fieldValue, null, 2)}
                                </pre>
                              </div>
                            ) : String(fieldValue).length > 100 ? (
                              <div className="bg-muted p-2 rounded text-xs max-h-24 overflow-y-auto">
                                <pre className="text-xs whitespace-pre-wrap font-mono">
                                  {String(fieldValue)}
                                </pre>
                              </div>
                            ) : (
                              <span className="font-mono">
                                {String(fieldValue)}
                              </span>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
        });
      }
    }

    return items;
  }, [row, getParsedAttributes]);

  // Virtualize the expanded content
  const contentVirtualizer = useVirtualizer({
    count: sections.length,
    getScrollElement: () => expandedContentRef.current,
    estimateSize: (index) => sections[index].height,
    overscan: 2,
  });

  return (
    <div
      ref={expandedContentRef}
      className="text-xs max-h-96 overflow-auto"
      style={{ height: "400px" }}
    >
      <div
        style={{
          height: `${contentVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {contentVirtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={contentVirtualizer.measureElement}
            className="absolute top-0 left-0 w-full p-2"
            style={{
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {sections[virtualItem.index].content}
          </div>
        ))}
      </div>
    </div>
  );
}
